import { parsePhoneNumberFromString } from 'libphonenumber-js/max';

import { normalizeUsPhoneToE164 } from './sms-marketing-contract';

export const CONTACT_PHONE_COUNTRIES = [
  { code: 'US', name: 'United States', callingCode: '1' },
  { code: 'GB', name: 'United Kingdom', callingCode: '44' },
  { code: 'DE', name: 'Germany', callingCode: '49' },
  { code: 'AE', name: 'United Arab Emirates', callingCode: '971' },
  { code: 'IL', name: 'Israel', callingCode: '972' },
  { code: 'NG', name: 'Nigeria', callingCode: '234' },
  { code: 'ZA', name: 'South Africa', callingCode: '27' },
] as const;

export type ContactPhoneCountry = (typeof CONTACT_PHONE_COUNTRIES)[number]['code'];
export type ContactVerificationChannel = 'sms' | 'call';

export function isContactPhoneCountry(country: string): country is ContactPhoneCountry {
  return CONTACT_PHONE_COUNTRIES.some(item => item.code === country);
}

export function contactPhoneCountry(input: string): ContactPhoneCountry | undefined {
  if (/[•*]/.test(input)) return CONTACT_PHONE_COUNTRIES.find(item => input.startsWith(`+${item.callingCode}`))?.code;
  const country = parsePhoneNumberFromString(input)?.country;
  return country && isContactPhoneCountry(country) ? country : undefined;
}

/** Keep the complete calling code so a masked contact destination retains its country. */
export function maskContactPhoneE164(input: string): string {
  const normalized = normalizeContactPhoneToE164(input);
  if (!normalized) return '***';
  const callingCode = parsePhoneNumberFromString(normalized)?.countryCallingCode;
  if (!callingCode) return '***';
  const prefixLength = Math.max(3, callingCode.length + 1);
  return `${normalized.slice(0, prefixLength)}${'*'.repeat(normalized.length - prefixLength - 2)}${normalized.slice(-2)}`;
}

export function contactPhoneNationalInput(input: string, country: ContactPhoneCountry): string {
  const phone = parsePhoneNumberFromString(input);
  return phone?.country === country ? phone.formatNational() : input;
}

export function contactPhoneFromNational(input: string, country: ContactPhoneCountry): string {
  if (input.trim().startsWith('+') || country === 'US' || !input.trim()) return input;
  const phone = parsePhoneNumberFromString(input, country);
  if (phone?.country === country && phone.isValid()) return phone.number;
  const callingCode = CONTACT_PHONE_COUNTRIES.find(item => item.code === country)?.callingCode;
  return callingCode ? `+${callingCode}${input.replace(/\D/g, '').replace(/^0/, '')}` : input;
}

/** National US input remains supported; all other destinations must include their country code. */
export function normalizeContactPhoneToE164(input: string): string | undefined {
  const trimmed = input.trim();
  if (!trimmed.startsWith('+') || trimmed.startsWith('+1')) {
    return normalizeUsPhoneToE164(trimmed);
  }
  if (!/^\+[\d\s().-]+$/.test(trimmed)) {
    return undefined;
  }
  const phone = parsePhoneNumberFromString(trimmed);
  if (!(phone?.country && isContactPhoneCountry(phone.country) && phone.isValid())) {
    return undefined;
  }
  const type = phone.getType();
  return type === 'MOBILE' || type === 'FIXED_LINE_OR_MOBILE' ? phone.number : undefined;
}

/**
 * The phone typed into a signup form, stored unverified. Wider than `normalizeContactPhoneToE164`, which gates SMS
 * verification: any real number worldwide is kept (national input is read as US), because until the member verifies it
 * where verification matters the number is only a way to reach them.
 */
export function normalizeSignupPhoneToE164(input: string): string | undefined {
  const trimmed = input.trim();
  if (!trimmed.startsWith('+') || trimmed.startsWith('+1')) {
    return normalizeUsPhoneToE164(trimmed);
  }
  if (!/^\+[\d\s().-]+$/.test(trimmed)) {
    return undefined;
  }
  const phone = parsePhoneNumberFromString(trimmed);
  return phone?.isValid() ? phone.number : undefined;
}
